import { ConfirmDialog as UiConfirmDialog } from '@chopchop-se/ui';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Destructive-action confirmation, i18n-wired.
 *
 * `@chopchop-se/ui`'s ConfirmDialog is translation-agnostic (labels are props),
 * so this wrapper fills the shared verbs from the `common` catalog and exposes
 * the promise form the app actually uses:
 *
 *     const { confirm, dialog } = useConfirm();
 *     …
 *     if (await confirm({ title: t('…'), message: t('…') })) {
 *       await mutation.mutateAsync(id);
 *     }
 *     …
 *     return <>{dialog}{page}</>;
 *
 * The promise resolves `false` on cancel/backdrop/Esc, so the call site reads as
 * straight-line code instead of a callback maze.
 */

export interface ConfirmOptions {
  title: string;
  message: string;
  variant?: 'danger' | 'warning' | 'success';
  confirmLabel?: string;
  cancelLabel?: string;
  processingLabel?: string;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

export interface UseConfirmResult {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Render this anywhere in the component's tree. */
  dialog: ReactNode;
}

export function useConfirm(): UseConfirmResult {
  const { t } = useTranslation(['common', 'shell']);
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve });
      }),
    [],
  );

  const settle = useCallback((confirmed: boolean) => {
    setPending((current) => {
      current?.resolve(confirmed);
      return null;
    });
  }, []);

  const dialog = pending ? (
    <UiConfirmDialog
      title={pending.title}
      message={pending.message}
      variant={pending.variant ?? 'danger'}
      confirmLabel={pending.confirmLabel ?? t('common:actions.confirm')}
      cancelLabel={pending.cancelLabel ?? t('common:actions.cancel')}
      processingLabel={pending.processingLabel ?? t('common:common.saving')}
      onConfirm={() => {
        settle(true);
      }}
      onClose={() => {
        settle(false);
      }}
    />
  ) : null;

  return { confirm, dialog };
}
